import { useTranslation } from 'react-i18next';
import { Heading, Tag } from '@digdir/designsystemet-react';
import { QuestionType, TemplateQuestion } from '../../../api/customSurveyTemplates';
import classes from '../CustomSurveyTemplateDetailPage.module.css';

/** Skalaen spørsmålet bruker, med standardverdiene når malen ikke har satt dem */
export const scaleOf = (question: TemplateQuestion) => {
  const isNps = question.questionType === QuestionType.NPS_SCALE;
  const min = question.scaleMin ?? (isNps ? 0 : 1);
  const max = question.scaleMax ?? (isNps ? 10 : 5);
  return Array.from({ length: Math.max(0, max - min + 1) }, (_, index) => min + index);
};

const isScale = (type: QuestionType) => type === QuestionType.LIKERT_SCALE || type === QuestionType.NPS_SCALE;
const isChoice = (type: QuestionType) => type === QuestionType.MULTIPLE_CHOICE || type === QuestionType.MULTIPLE_SELECT;

const Answer = ({ question }: { question: TemplateQuestion }) => {
  const { t } = useTranslation();
  const type = question.questionType;

  if (isScale(type)) {
    const scale = scaleOf(question);
    const min = scale[0];
    const max = scale[scale.length - 1];
    const labels = [
      question.scaleMinLabel && `${min} = ${question.scaleMinLabel}`,
      question.scaleMaxLabel && `${max} = ${question.scaleMaxLabel}`,
    ].filter(Boolean);
    return (
      <div className={classes.answer}>
        <div aria-hidden="true" className={classes.scale} style={{ gridTemplateColumns: `repeat(${scale.length}, minmax(0, 1fr))` }}>
          {scale.map((value) => (
            <span key={value}>{value}</span>
          ))}
        </div>
        <span className={classes.hint}>
          {t('customSurveys.templateDetail.question.scaleRange', { min, max })}
          {labels.length > 0 && ` · ${labels.join(' · ')}`}
        </span>
      </div>
    );
  }

  if (isChoice(type)) {
    const many = type === QuestionType.MULTIPLE_SELECT;
    return (
      <div className={classes.answer}>
        <ul className={classes.options}>
          {[...(question.options ?? [])]
            .sort((a, b) => a.orderIndex - b.orderIndex)
            .map((option, index) => (
              <li key={option.id ?? index}>
                <span aria-hidden="true" className={many ? classes.checkbox : classes.radio} />
                {option.optionText}
              </li>
            ))}
        </ul>
        <span className={classes.hint}>
          {t(many ? 'customSurveys.templateDetail.question.pickMany' : 'customSurveys.templateDetail.question.pickOne')}
        </span>
      </div>
    );
  }

  if (type === QuestionType.YES_NO) {
    return (
      <div aria-hidden="true" className={`${classes.scale} ${classes.yesNo}`}>
        <span>{t('customSurveys.templateDetail.question.yes')}</span>
        <span>{t('customSurveys.templateDetail.question.no')}</span>
      </div>
    );
  }

  return <div aria-hidden="true" className={classes.textBox} data-long={type === QuestionType.LONG_TEXT} />;
};

/** Ett spørsmål slik den som svarer ser det, med nummer, type og om det må besvares */
export const QuestionPreview = ({ question, number }: { question: TemplateQuestion; number: number }) => {
  const { t } = useTranslation();

  return (
    <li className={classes.question}>
      <span aria-hidden="true" className={classes.number}>
        {number}
      </span>
      <div className={classes.questionBody}>
        <div className={classes.questionHead}>
          <Heading level={3} data-size="xs">
            <span className="ds-sr-only">{t('customSurveys.templateDetail.question.number', { number })} </span>
            {question.questionText}
          </Heading>
          <div className={classes.tags}>
            <Tag data-size="sm" data-color="accent">
              {t(`customSurveys.templateDetail.question.types.${question.questionType}`)}
            </Tag>
            {question.isRequired && (
              <Tag data-size="sm" data-color="neutral">
                {t('customSurveys.templateDetail.question.required')}
              </Tag>
            )}
            {question.category && (
              <Tag data-size="sm" data-color="neutral">
                {question.category}
              </Tag>
            )}
          </div>
        </div>
        <Answer question={question} />
      </div>
    </li>
  );
};
